// Create / edit / delete dialogs across the CRUD pages.
//
// UX-021: the header used to claim this handled "a focus return to the
// trigger after close" and kept "Tab focus inside the modal panel". Neither
// was true — there was no previously-focused ref and no focus trap, so Tab
// walked straight out into the page behind and closing dropped focus to the
// top of the document. Fourteen instances across seven pages inherited that.
//
// The behaviour now comes from <Dialog>, which is DangerousActionButton's
// implementation extracted, so there is one modal contract instead of three
// and this comment cannot drift away from the code again.

import {
  cloneElement,
  isValidElement,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Button, Dialog, Icon } from '@ng-unifi/ui';

type ModalTone = 'default' | 'destructive';

interface CrudModalProps {
  title: string;
  icon: string;
  tone?: ModalTone;
  labelledById?: string;
  submitting: boolean;
  error: string | null;
  submitLabel?: string;
  cancelLabel?: string;
  canSubmit?: boolean;
  onCancel: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  /** Optional footer extras rendered to the left of Cancel/Submit. */
  footerExtras?: ReactNode;
}

export function CrudModal({
  title,
  icon,
  tone = 'default',
  labelledById = 'crud-modal-title',
  submitting,
  error,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  canSubmit = true,
  onCancel,
  onSubmit,
  children,
  footerExtras,
}: CrudModalProps) {
  return (
    <Dialog
      title={title}
      onClose={onCancel}
      // Escape and backdrop dismissal stay blocked mid-submission, and on a
      // destructive confirm, so a stray key cannot silently cancel "delete
      // this household". Both were deliberate before and are kept.
      dismissible={!submitting && tone !== 'destructive'}
      className="max-w-[min(92vw,520px)] p-6 sm:p-8"
      header={
        <header className="flex items-center gap-3">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${
              tone === 'destructive'
                ? 'bg-error-container text-on-error-container'
                : 'bg-primary-container text-on-primary'
            }`}
          >
            <Icon name={icon} />
          </div>
          <h2
            id={labelledById}
            className="font-headline-sm text-headline-sm text-on-surface"
          >
            {title}
          </h2>
        </header>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {children}
        {error ? (
          <p role="alert" className="text-error font-body-md text-body-md">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-2 pt-2">
          <div>{footerExtras}</div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={!canSubmit}
              variant={tone === 'destructive' ? 'danger' : 'primary'}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * UX-024: this computed `hintId` and `errorId` and then applied neither, so
 * every hint and every validation message in every CRUD dialog sat next to its
 * field visually and was unrelated to it programmatically. `aria-describedby`
 * appeared exactly once in the whole application. A screen-reader user
 * submitting an invalid form heard nothing about why.
 *
 * The ids are now wired onto the field by cloning the child, which is what the
 * original code was clearly reaching for. Callers pass a plain input; they do
 * not have to remember to describe it. A child that already sets
 * `aria-describedby` keeps its own value.
 */
export function FormField({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  /** Error text rendered below the field in the error color. */
  error?: string | null;
  /** Adds a visible "*" to the label and an sr-only "required" hint. */
  required?: boolean;
  children: ReactNode;
}) {
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  const described = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        'aria-describedby':
          (children.props as Record<string, unknown>)['aria-describedby'] ?? describedBy,
        // An invalid field must announce itself as invalid, not merely look red.
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })
    : children;

  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className="font-label-md text-label-md text-on-surface block"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="text-error ml-0.5">
            *
          </span>
        ) : null}
        {required ? <span className="sr-only"> (required)</span> : null}
      </label>
      {described}
      {hint && !error ? (
        <p
          id={hintId}
          className="font-body-md text-body-md text-on-surface-variant"
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="font-body-md text-body-md text-error"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Simple email-format check shared between forms. Mirrors the
 * back-end Zod email validator's intent — "must look like an email" —
 * without being RFC-pedantic. Used for inline hints so users don't
 * submit obvious typos.
 */
export function isLikelyEmail(value: string): boolean {
  // Reject if it doesn't have exactly one @ with non-empty local and
  // domain parts, or if the domain doesn't contain a dot.
  const at = value.indexOf('@');
  if (at < 1 || at !== value.lastIndexOf('@')) return false;
  const domain = value.slice(at + 1);
  if (!domain.includes('.')) return false;
  if (domain.startsWith('.') || domain.endsWith('.')) return false;
  return true;
}
